// src/features/HomePage/components/AboutProject.tsx

import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { BREAKPOINTS } from "../../../utils/styles/breakpoints";
import { COLORS } from "../../../utils/styles/colors";
import {
  HeadlineSStyling,
  HeadlineMStyling,
  HeadlineLStyling,
} from "../../../utils/styles/typography";

// Styled Components
const ImageContainer = styled.div`
  background-color: ${COLORS.CustomSkin};
  border-radius: 20px;
  padding: 10px;
  overflow: hidden;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  margin-bottom: 24px;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 10px;
  }
`;

// Modified PageWrapper for split background
const PageWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  z-index: 0;
  overflow: hidden;
  background: ${COLORS.CustomGreen};
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  ${HeadlineMStyling()};
  margin-bottom: 24px;
  text-align: center;
  color: ${COLORS.White};
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.7), 0 1px 0 ${COLORS.CustomSkin},
    0 0 2px ${COLORS.CustomSkin}, 0 0 8px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  padding: 18px 0;
  letter-spacing: 1.5px;
  border: none;
  background: none;
  box-shadow: none;

  ${BREAKPOINTS.LARGE} {
    ${HeadlineLStyling()};
    text-align: center;
    color: ${COLORS.White};
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  ${BREAKPOINTS.LARGE} {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const TextContent = styled.div`
  p {
    margin-bottom: 16px;
    line-height: 1.6;
    font-size: 1.15rem;
    color: ${COLORS.White};
    font-weight: 600;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.55), 0 1px 0 ${COLORS.CustomSkin};
    border-radius: 8px;
    padding: 0;
    background: none;
  }
`;

const ImageWrapper = styled.div`
  background-color: ${COLORS.CustomSkin};
  border-radius: 20px;
  padding: 10px;
  overflow: hidden;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 10px;
  }
`;

const SectionParagraph = styled.p`
  font-size: 1.15rem;
  font-weight: 500;
  margin-bottom: 16px;
  color: ${COLORS.White};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.55), 0 1px 0 ${COLORS.CustomSkin};
`;

const BulletList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  li {
    ${HeadlineSStyling()};
    margin-bottom: 8px;
    position: relative;
    padding-left: 20px;
    color: ${COLORS.White};
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.55), 0 1px 0 ${COLORS.CustomSkin};
    &::before {
      content: "•";
      position: absolute;
      left: 0;
      color: ${COLORS.CustomSkin};
      text-shadow: none;
    }
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 16px;
  position: relative;
  z-index: 2;

  ${BREAKPOINTS.LARGE} {
    padding: 64px 0;
  }
`;

const AboutProject = () => {
  const { t } = useTranslation();

  return (
    <PageWrapper>
      <ContentContainer>
        <Section>
          <SectionTitle>{t("პროექტის შესახებ")}</SectionTitle>
          <TwoColumnGrid>
            <TextContent>
              <p>
                {t(
                  "Samgori Park Residence - თანამედროვე საცხოვრებელი სივრცე, რომელიც პროექტირებულია გამორჩეული ცხოვრების სტილისთვის."
                )}
              </p>
              <p>
                {t(
                  "პროექტი მდებარეობს თბილისის ერთ-ერთ მთავარ სატრანსპორტო არტერიაზე – მოსკოვის გამზირზე, რაც უზრუნველყოფს მარტივ წვდომას როგორც საზოგადოებრივ ტრანსპორტზე, ისე ქალაქის ყველა მნიშვნელოვან მიმართულებაზე."
                )}
              </p>
            </TextContent>
            <ImageWrapper>
              <img
                src="/images/main.jpg"
                alt="Samgori Park Residence Building"
              />
            </ImageWrapper>
          </TwoColumnGrid>
        </Section>

        {/* --- Location and Infrastructure Section --- */}
        <Section>
          <SectionTitle>{t("ლოკაცია და ინფრასტრუქტურა")}</SectionTitle>
          <TwoColumnGrid>
            <div>
              <SectionParagraph>
                {t(
                  "Samgori Park Residence მდებარეობს სამგორის ცენტრში, მოსკოვის გამზირის N31ა-ში – რაც პროექტს შესანიშნავ სატრანსპორტო ხელმისაწვდომობას და განვითარებულ ინფრასტრუქტურას ანიჭებს."
                )}
              </SectionParagraph>
              <SectionParagraph>
                <BulletList>
                  <li>{t('მეტროსადგური "სამგორი" - 300 მეტრში;')}</li>
                  <li>{t("მეტროსადგური ''ვარკეთილი'' – 1500 მეტრში;")}</li>
                  <li>{t("3 საჯარო და 2 კერძო სკოლა;")}</li>
                  <li>{t("საბავშვო ბაღები;")}</li>
                  <li>{t("სხვადასხვა სავაჭრო და სერვისცენტრები;")}</li>
                  <li>{t("საზოგადოებრივი ტრანსპორტის მარშრუტები;")}</li>
                  <li>{t("სამედიცინო და სპორტული ინფრასტრუქტურა.")}</li>
                </BulletList>
              </SectionParagraph>
              <SectionParagraph>
                {t(
                  "ეს ყველაფერი უზრუნველყოფს კომფორტულ და ფუნქციურ საცხოვრებელ გარემოს, რომელიც იდეალურია როგორც საცხოვრებლად, ისე საინვესტიციოდ."
                )}
              </SectionParagraph>
            </div>
            <ImageContainer>
              <img
                src="/images/location.jpg"
                alt="Project location and infrastructure"
              />
            </ImageContainer>
          </TwoColumnGrid>
        </Section>

        {/* --- Architecture Section --- */}
        <Section>
          <SectionTitle>{t("არქიტექტურა და დაგეგმარება")}</SectionTitle>
          <SectionParagraph>
            {t(
              "Samgori Park Residence-ს არქიტექტურა დამყარებულია თანამედროვე ურბანული დიზაინის პრინციპებზე - მაქსიმალურად გამოყენებული სივრცე, დღის შუქზე ორიენტირებული ბინები და ვიზუალურად ჰარმონიული ბლოკები."
            )}
          </SectionParagraph>
          <BulletList>
            <li>{t("3 საცხოვრებელი ბლოკი")}</li>
            <li>{t("თითოეული ბლოკი 16 საცხოვრებელ სართულს მოიცავს;")}</li>
            <li>{t("სართულზე განთავსებულია 13 ბინა;")}</li>
            <li>{t("ბინები წარმოდგენილია 51 კვ.მ-დან - 145 კვ.მ-მდე;")}</li>
            <li>
              {t(
                "გეგმარება გამორჩეულად პრაქტიკულია: ცალკე გამოყოფილი საძინებლები, ღია მისაღები ზონები, ბუნებრივი განათება და აივნები გამორჩეული ხედით."
              )}
            </li>
          </BulletList>
        </Section>

        {/* --- Recreational and Common Spaces Section --- */}
        <Section>
          <SectionTitle>{t("რეკრეაციული და საერთო სივრცეები")}</SectionTitle>
          <SectionParagraph>
            {t(
              "პროექტის ერთ-ერთი მთავარი უპირატესობაა სუფთა, ღია და მწვანე სივრცე:"
            )}
          </SectionParagraph>
          <BulletList>
            <li>{t("საბავშვო მოედნები უსაფრთხო საფარით;")}</li>
            <li>
              {t(
                "ღია სპორტული ინფრასტრუქტურა: სავარჯიშო ზონა, ფეხბურთისა და კალათბურთის მოედანი;"
              )}
            </li>
            <li>
              {t("დასასვენებელი ბაღი და ბილიკები (ჯამში 4500 კვ. მ.-ზე მეტი);")}
            </li>
            <li>{t("დახურული ეზო - Gated Community.")}</li>
          </BulletList>
          <ImageContainer>
            <img
              src="/images/recreation.jpg"
              alt="Recreational and common spaces"
            />
          </ImageContainer>
        </Section>

        {/* --- Stadium Section --- */}
        <Section>
          <SectionTitle>{t("საბავშვო სივრცე")}</SectionTitle>
          <SectionParagraph>
            {t(
              "სამგორი პარკ რეზიდენსში საბავშვო მოედანი დაგხვდებათ, სადაც თქვენი პატარები დროს ხალისიანად გაატარებენ და უსაფრთხო გარემოში ითამაშებენ"
            )}
          </SectionParagraph>
          <ImageContainer>
            <img src="/images/kids-place.jpg" alt="Kids playground" />
          </ImageContainer>
        </Section>

        {/* --- Stadium Section --- */}
        <Section>
          <SectionTitle>{t("სტადიონი")}</SectionTitle>
          <SectionParagraph>
            {t(
              "მრავალფუნქციური სტადიონი სპორტის, კულტურისა და საზოგადოებისთვის"
            )}
          </SectionParagraph>
          <ImageContainer>
            <img src="images/stadium.jpg" alt="Stadium" />
          </ImageContainer>
        </Section>

        {/* --- Construction Standard Section --- */}
        <Section>
          <SectionTitle>
            {t("მშენებლობის სტანდარტი და ტექნოლოგია")}
          </SectionTitle>
          <SectionParagraph>
            {t(
              "Samgori Park Residence-ის მშენებლობაში გამოყენებულია უმაღლესი ხარისხის მასალები და თანამედროვე სამშენებლო ტექნოლოგიები:"
            )}
          </SectionParagraph>
          <BulletList>
            <li>{t("მონოლითური კონსტრუქცია;")}</li>
            <li>{t("ენერგოეფექტური ალუმინის სახურავი და ფანჯრები;")}</li>
            <li>{t("კედლების თბო და ხმის იზოლაცია;")}</li>
            <li>{t("პრემიუმ ბრენდის ლიფტები;")}</li>
            <li>
              {t("თანამედროვე უსაფრთხოების სისტემა და ვიდეომეთვალყურეობა.")}
            </li>
          </BulletList>
        </Section>

        {/* --- Parking Section --- */}
        <Section>
          <SectionTitle>{t("შიდა და გარე პარკინგი")}</SectionTitle>
          <SectionParagraph>
            {t(
              "პროექტის შიდა ტერიტორიაზე მოეწყობა როგორც შიდა ასე გარე საპარკინგე სივრცეები."
            )}
          </SectionParagraph>
          {/* <TwoColumnGrid>
            <ImageContainer>
              <img
                src="/images/parking1.jpg"
                alt="Internal and external parking"
              />
            </ImageContainer>
            <ImageContainer>
              <img
                src="/images/parking2.jpg"
                alt="Internal and external parking"
              />
            </ImageContainer>
          </TwoColumnGrid> */}
        </Section>
      </ContentContainer>
    </PageWrapper>
  );
};

export default AboutProject;
